/* @flow */
import * as React from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';
import { keys } from 'lodash';
import gql from 'graphql-tag';
import invariant from 'invariant';

import { showToast, showAPIErrorToast } from 'reducers/toasts';
import CurrentUser from 'auth/CurrentUser';
import errorFragment from 'queries/errorFragment.gql';
import Glyph from 'components/Glyph';
import humanparser from 'humanparser';
import Input from 'components/Input';
import KeyConstants from 'constants/KeyConstants';
import LayerTrigger from 'components/LayerTrigger';
import Link from 'components/Link';
import Menu from 'components/Menu';
import MenuItem from 'components/MenuItem';
import messageFromErrorFragment from 'lib/messageFromErrorFragment';
import ProfilePictureUploadButton from 'user/components/ProfilePictureUploadButton';
import SettingsStore from 'stores/SettingsStore';
import Text from 'components/Text';
import UserActions from 'user/UserActions';
import UserAvatar from 'users/components/UserAvatar';

import './styles/ProfileHeaderView.scss';

type Props = {
  showAPIErrorToast: (error: *) => *,
  showToast: (text: string, kind: 'success' | 'error') => *,
  update: (attributes: Object) => Promise<*>,
  data: {
    loading: boolean,
    user?: {
      role: string,
      display_name: string,
      grade_level: {
        code: string,
        name: string,
        short_name: string,
      },
    },
  },
};

type State = {
  name?: string,
  gradeLevel?: {
    code: string,
    name: string,
    short_name: string,
  },
};

class ProfileHeaderView extends React.Component<Props, State> {
  componentWillMount() {
    if (this.props.data.loading === false) {
      this.initiateStateFromLoadedProps(this.props);
    }
  }

  componentWillReceiveProps(nextProps: Props) {
    if (keys(this.state).length === 0 && nextProps.data.loading === false) {
      this.initiateStateFromLoadedProps(nextProps);
    }
  }

  initiateStateFromLoadedProps(props: Props) {
    const { user } = props.data;
    invariant(user, 'ProfileHeaderView - initiateStateFromProps called without loaded props');

    this.setState({
      name: user.display_name,
      gradeLevel: user.grade_level,
    });
  }

  handleChange = (event: any) => {
    this.setState({ name: event.target.value });
  };

  handleBlur = () => {
    const { user } = this.props.data;
    const { name } = this.state;

    invariant(user, 'ProfileHeaderView - onBlur but user missing');

    if (!name || name.length === 0) {
      this.props.showToast('Signature cannot be empty', 'error');
      this.restoreStateToUnedited();
      return;
    }

    if (name === user.display_name) {
      return;
    }

    const save = user.role === 'teacher' ? this.saveSignature : this.saveName;

    save(name);
  };

  // on API error, we restore the state back to the unedited version.
  restoreStateToUnedited = () => this.initiateStateFromLoadedProps(this.props);

  saveSignature = (name: string) => {
    this.props
      .update({ signature: name })
      .then(
        () => this.props.showToast('Signature saved', 'success'),
        e => this.props.showAPIErrorToast(e) && this.restoreStateToUnedited()
      );
  };

  saveName = (name: string) => {
    const human = humanparser.parseName(name);

    const data = {
      first_name: human.firstName,
      last_name: human.lastName || '',
    };

    this.props
      .update(data)
      .then(
        () => this.props.showToast('Name saved', 'success'),
        e => this.props.showAPIErrorToast(e) && this.restoreStateToUnedited()
      );
  };
  renderEditableDisplayName() {
    const onKeyDown = event => {
      if (event.keyCode === KeyConstants.ENTER) {
        this.refs.displayNameInput.getInputNode().blur();
      }
    };

    return (
      <div className="ProfileHeaderView-inputContainer">
        <Glyph name="pencil-stroke" color="eclipse" size="large" />
        <Input
          ref="displayNameInput"
          className="ProfileHeaderView-displayName"
          value={this.state.name}
          onBlur={this.handleBlur}
          onKeyDown={onKeyDown}
          onChange={this.handleChange}
        />
      </div>
    );
  }

  renderGradeByline() {
    const { gradeLevel } = this.state;
    const gradeText = gradeLevel ? gradeLevel.name : 'Set your grade level';
    const setGrade = grade => {
      const gradeObject = SettingsStore.getSetting('grades').find(g => g.code === grade);
      this.setState({ gradeLevel: gradeObject });
      this.props
        .update({ grade_level: grade })
        .then(
          () => this.props.showToast('Grade level saved', 'success'),
          e => this.props.showAPIErrorToast(e)
        );
    };

    const menu = (
      <Menu className="ProfileHeaderView-gradeMenu">
        {SettingsStore.getSetting('grades').map(grade => (
          <MenuItem key={grade.code} onSelect={setGrade}>
            {grade.name}
          </MenuItem>
        ))}
      </Menu>
    );

    return (
      <div className="ProfileHeaderView-byline">
        <LayerTrigger layer={menu} trigger="click" placement="bottom" alignment="middle">
          <Link href="#">
            <span className="ProfileHeaderView-gradeLabel">
              <Text color={gradeLevel ? 'thunder' : 'flame'} size="medium">
                {gradeText}
              </Text>
              <Glyph name="chevron-down" color="thunder" size="xsmall" />
            </span>
          </Link>
        </LayerTrigger>
      </div>
    );
  }

  renderSignatureByline() {
    return (
      <div className="ProfileHeaderView-byline">
        <Text italic color="thunder" size="xsmall">
          Your signature is what others will see when you send a message.
        </Text>
      </div>
    );
  }

  render() {
    if (this.props.data.loading) {
      return null;
    }

    const { user } = this.props.data;
    invariant(user, 'ProfileHeaderView - data loaded but user missing');

    const canAddProfilePicture = CurrentUser.getUser().canAddProfilePicture();

    return (
      <div className="ProfileHeaderView">
        {canAddProfilePicture ? (
          <ProfilePictureUploadButton />
        ) : (
          <UserAvatar user={CurrentUser.getUser().toJSON()} />
        )}
        {this.renderEditableDisplayName()}
        {user.role === 'student' && this.renderGradeByline()}
        {user.role === 'teacher' && this.renderSignatureByline()}
      </div>
    );
  }
}

const withMutations = graphql(
  gql`
    mutation updateUser($input: UpdateUserInput!) {
      updateUser(input: $input) {
        user {
          id
          uuid
          signature
          last_name
          first_name
          display_name
          grade_level {
            code
            name
            short_name
          }
        }
        error {
          ...errorFragment
        }
      }
    }

    ${errorFragment}
  `,
  {
    props({ mutate }) {
      return {
        update(input: Object) {
          return mutate({ variables: { input } }).then(response => {
            const { user, error } = response.data.updateUser;

            if (error) {
              throw new Error(messageFromErrorFragment(error));
            }

            UserActions.updateLocal({
              signature: user.signature,
              last_name: user.last_name,
              first_name: user.first_name,
              display_name: user.display_name,
              grade_level: user.grade_level,
            });
          });
        },
      };
    },
  }
);

const withUser = graphql(
  gql`
    query UserNamesAndGradeLevel {
      user: me {
        id
        uuid
        role
        display_name
        grade_level {
          code
          name
          short_name
        }
      }
    }
  `
);

function mapDispatchToProps(dispatch: Function) {
  // $FlowIssue
  return bindActionCreators({ showToast, showAPIErrorToast }, dispatch);
}

export { ProfileHeaderView };
export default withMutations(
  withUser(
    connect(
      undefined,
      mapDispatchToProps
    )(ProfileHeaderView)
  )
);
